import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosConfig from '../../utils/axiosConfig';

export const fetchChatUnreadCount = createAsyncThunk(
	'chat/unreadCount',
	async (_, { rejectWithValue }) => {
		try {
			const response = await axiosConfig.post('/chat/unread-count', {});
			const n = response.data?.count;
			return typeof n === 'number' ? n : 0;
		} catch (error) {
			return rejectWithValue({
				error: error.response?.data?.message ?? error.message,
			});
		}
	},
);

export const markChatRead = createAsyncThunk(
	'chat/markRead',
	async (chatId, { rejectWithValue }) => {
		try {
			await axiosConfig.post('/chat/mark-read', { id: chatId });
			return String(chatId);
		} catch (error) {
			return rejectWithValue({
				error: error.response?.data?.message ?? error.message,
			});
		}
	},
);

export const sendChatMessage = createAsyncThunk('chat/send/message', async (message, { rejectWithValue, getState }) => {
	try {

		const response = await axiosConfig.post('/chat/send', {
			message: message,
			from: getState().profile.currentProfile._id,
			to: getState().chat.currentChat.profile
		}, {
			withCredentials: false
		});

		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const sendFirstMessage = createAsyncThunk('chat/send/first/message', async ({message, to}, { rejectWithValue, getState }) => {
	try {

		const response = await axiosConfig.post('/chat/send', {
			message: message,
			from: getState().profile.currentProfile._id,
			to: to
		}, {
			withCredentials: false
		});

		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const deleteChatMessage = createAsyncThunk('chat/delete/message', async (id, { rejectWithValue, getState }) => {
	try {
		let profile = getState().profile.currentProfile._id
		const response = await axiosConfig.post('/chat/delete', {
			id: id,
			profile: profile
		}, {
			withCredentials: false
		});

		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const getChatFiles = createAsyncThunk('chat/files', async (id, { rejectWithValue, getState }) => {
	try {
		let profile = getState().profile.currentProfile._id
		const response = await axiosConfig.post('/chat/files', {
			id: id,
			profile: profile
		}, {
			withCredentials: false
		});

		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const sendChatFile = createAsyncThunk('chat/send/file', async (file, { rejectWithValue, getState }) => {
	try {
		let formData = new FormData();
		formData.append("from",  getState().profile.currentProfile._id);
		formData.append("to",  getState().chat.currentChat.profile);
		formData.append("file", file);

		const response = await axiosConfig.post('/chat/file', formData, {
			withCredentials: false,
			headers: {
				'Content-Type': 'multipart/form-data'
			}
		});
		getConversationList();
		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const getChatMessages = createAsyncThunk(
	'chat/messages',
	async (id, { rejectWithValue, dispatch }) => {
		try {
			const response = await axiosConfig.post('/chat/messages', { id });
			dispatch(fetchChatUnreadCount());
			return response.data;
		} catch (error) {
			return rejectWithValue({
				error: error.response.data ? error.response.data.message : error.message,
			});
		}
	},
);

export const getConversationList = createAsyncThunk('chat/list', async (_, { rejectWithValue, getState }) => {
	try {
		const response = await axiosConfig.post('/chat/list', {
			profile: getState().profile.currentProfile._id,

		});

		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

/** Mark open chat read on server, then refresh list + global unread (used from socket `message`). */
export const handleSocketIncomingMessage = createAsyncThunk(
	'chat/socketIncoming',
	async (payload, { dispatch, getState }) => {
		const chatId = payload?.chat != null ? String(payload.chat) : '';
		const open = getState().chat?.currentChat;
		const openId = open?._id != null ? String(open._id) : '';
		if (chatId && openId && chatId === openId) {
			try {
				await dispatch(markChatRead(chatId)).unwrap();
			} catch {
				// ignore
			}
		}
		try {
			await dispatch(getConversationList()).unwrap();
		} catch {
			// ignore (e.g. wrong profile context)
		}
		await dispatch(fetchChatUnreadCount());
	},
);

export const getAdminSupport = createAsyncThunk('chat/admin/list', async (_, { rejectWithValue, getState }) => {
	try {
		const response = await axiosConfig.post('/chat/admin/list', {
			profile: "420420420420420420420420",
		}, {
			withCredentials: false
		});

		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});

export const getFullChat = createAsyncThunk(
	'chat/single',
	async (arg, { rejectWithValue, dispatch }) => {
		const id = typeof arg === 'string' ? arg : arg?.id;
		const perspectiveProfileId =
			typeof arg === 'object' && arg != null ? arg.perspectiveProfileId : undefined;
		if (!id) {
			return rejectWithValue({ error: 'Missing chat id' });
		}
		try {
			const body = { id };
			if (perspectiveProfileId != null && String(perspectiveProfileId).length > 0) {
				body.perspectiveProfileId = String(perspectiveProfileId);
			}
			const response = await axiosConfig.post('/chat/single', body, {
				withCredentials: false,
			});
			dispatch(fetchChatUnreadCount());
			return response.data;
		} catch (error) {
			return rejectWithValue({
				error: error.response?.data?.message ?? error.message,
			});
		}
	},
);

export const goToSupport = createAsyncThunk('chat/support', async (_, { rejectWithValue, getState }) => {
	try {
		const response = await axiosConfig.post('/chat/support', {
			profile: getState().profile.currentProfile._id,
		}, {
			withCredentials: false
		});

		return response.data;
	} catch (error) {
		return rejectWithValue({
			error: error.response.data ? error.response.data.message : error.message
		});
	}
});
